/** Body parts every creature shares (05 §6.1): eyes, feet, small props. Local frame: facing +x, +z toward the camera. */

import { type Color, Matrix4 } from 'three';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import type { Palette } from '../palette';

/**
 * One big cartoon eye: a white ball with a black pupil toward the front. `size` is its
 * height in tiles; at 14 texels a tile, 0.4 is a 5-6 px eye, the smallest that reads.
 */
export function eye(mb: MeshBuilder, p: Palette, x: number, y: number, z: number, size = 0.4, pupil?: Color): void {
  const r = size / 2;
  mb.blob(r * 0.72, r, r * 0.6, p.color('eye.1'), place(x, y, z), { detail: 1 });
  mb.box(r * 0.62, r * 1.05, r * 0.6, pupil ?? p.color('eye.0'), place(x + r * 0.5, y - r * 0.55, z + r * 0.18));
}

/** A pair of eyes either side of z = 0. */
export function eyes(mb: MeshBuilder, p: Palette, x: number, y: number, spread: number, size = 0.4, pupil?: Color): void {
  eye(mb, p, x, y, spread, size, pupil);
  eye(mb, p, x, y, -spread, size, pupil);
}

/** A stubby foot, built round its own pivot so a pose can swing it. */
export function foot(p: Palette, colour: string, length = 0.5): MeshBuilder {
  const mb = new MeshBuilder();
  mb.blob(length / 2, 0.14, 0.18, p.color(colour), place(0.06, -0.12, 0), { flatBottom: true, detail: 0 });
  return mb;
}

/** A cone pointing along +y from (x, y), turned by `roll` about z. */
export function spike(mb: MeshBuilder, x: number, y: number, roll: number, size: number, c: Color, base?: Color): void {
  const m = new Matrix4().makeTranslation(x, y, 0).multiply(new Matrix4().makeRotationZ(roll));
  mb.cylinder(size * 0.55, 0, size, 4, c, m, base ?? c);
}

/** The colour ramp of a creature's body: its own, or the shared angry red once it has escaped a bubble. */
export const bodyRamp = (own: string, angry: boolean): string => (angry ? 'angry' : own);
